import type { ObjectCategory } from '../../content/registry/objectRegistry';
import type {
  CharacterDefinition,
  CharacterPoseLibrary,
} from '../../content/schema/character';
import {
  CURRENT_MAP_VERSION,
  type MapDocument,
  type MapObjectInstance,
  type MapObjectTransform,
  type Sprite2D,
  type Tilemap2D,
  type Tilemap2DEntity,
  type Tilemap2DTile,
} from '../../content/schema/map';
import type { PlayerConfig } from '../../runtime/FirstPersonController';
import type { MapRuntimeOptions } from '../../runtime/mapRuntime';
import type { SceneTheme } from '../../runtime/GameRuntime';

/**
 * Project creation templates surfaced in the dashboard. Only generic,
 * non-proprietary starters are offered: Soulstack-owned games (Sunder, The
 * Dwindling) are never templates, their maps live as private game data.
 */
export type StudioProjectTemplate = 'blank' | 'gallery' | 'village' | 'platformer';

export interface StudioProjectMeta {
  id: string;
  name: string;
  ownerUid: string;
  template: StudioProjectTemplate;
  /** Epoch millis. */
  createdAt: number;
  /** Epoch millis. */
  updatedAt: number;
  /** Storage download URL for a project card thumbnail, if generated. */
  thumbnailUrl?: string;
  /** The creator's uploaded cover image (config.cover.url); null when none
   *  has been uploaded, and a generated placeholder is shown instead. */
  coverUrl?: string | null;
}

export type StudioAssetKind = 'glb' | 'texture' | 'audio';

export interface StudioAssetBounds {
  /** Axis-aligned size in metres. */
  size: [number, number, number];
  /** Centre offset of the bounding box from the model origin. */
  center: [number, number, number];
}

/**
 * A user-uploaded asset. GLB models register a `gltf`-renderer ObjectDefinition
 * into the project registry whose params carry `downloadUrl`.
 */
export interface StudioAssetRecord {
  id: string;
  name: string;
  kind: StudioAssetKind;
  /** Path within the user's Storage bucket. */
  storagePath: string;
  /** Resolved download URL used by the renderer. */
  downloadUrl: string;
  /** Extracted bounds (GLB) - drives the registered definition's bounds. */
  bounds: StudioAssetBounds;
  category: ObjectCategory;
  thumbnailUrl?: string;
  /** Byte size of the stored file. */
  sizeBytes: number;
  /** Epoch millis. */
  createdAt: number;
  /** Decoded duration in seconds (audio assets), extracted at upload. */
  durationSeconds?: number;
}

/** The game dimension a project targets; one studio adapts to either. */
export type StudioProjection = '2D' | '3D';

export interface StudioProjectConfig {
  theme: SceneTheme;
  player?: Partial<PlayerConfig>;
  runtime?: MapRuntimeOptions;
  /** Chosen at creation, changeable in Game Setup. Defaults to '3D'. */
  projection?: StudioProjection;
  /** Environment-mode choices the theme was derived from. */
  environment?: { preset: string; time: number; fog: number; sun?: number; bloom?: number; vignette?: number };
  /** Cover image (key art): an uploaded image asset shown on the workspace
   * and the public play landing. The URL is stored so the play surface needs
   * no asset lookup. */
  cover?: { assetId: string; url: string };
}

/**
 * The complete in-memory shape of a Studio project. Persisted across several
 * Firestore documents (meta on the project doc; map + characters in a
 * `documents` subcollection; assets + snapshots in their own subcollections) so
 * the 1 MiB-per-document ceiling is never approached by a single field.
 */
export interface StudioProject {
  meta: StudioProjectMeta;
  config: StudioProjectConfig;
  map: MapDocument;
  characters: CharacterDefinition[];
  poseLibraries: CharacterPoseLibrary[];
  /** Ordered bone ids per rig, for any character beyond the built-in neutral biped. */
  rigBones: Record<string, string[]>;
  assets: StudioAssetRecord[];
}

/** A neutral, empty single-floor map for the Blank project template. */
export function createBlankMapDocument(projectId: string): MapDocument {
  return {
    id: `${projectId}-map`,
    name: 'Untitled Map',
    version: CURRENT_MAP_VERSION,
    floors: [
      {
        id: 'ground',
        level: 0,
        label: 'Ground Floor',
        panels: [
          { id: 'ground-floor', center: [0, 0], size: [20, 20], color: '#3a4048', ceilingHeight: 3 },
        ],
        walls: [
          { id: 'w-n', position: [0, 1.5, -10], size: [20, 3, 0.3], color: '#4a525c' },
          { id: 'w-s', position: [0, 1.5, 10], size: [20, 3, 0.3], color: '#4a525c' },
          { id: 'w-e', position: [10, 1.5, 0], size: [0.3, 3, 20], color: '#4a525c' },
          { id: 'w-w', position: [-10, 1.5, 0], size: [0.3, 3, 20], color: '#4a525c' },
        ],
        blockers: [],
        areas: [{ id: 'ground-room', name: 'Room', floor: 0, kind: 'room' }],
      },
    ],
    objectInstances: [],
    stairTriggers: [],
    settings: { defaultSpawn: { x: 0, y: 0, z: 0, yaw: 0, floor: 0 } },
  };
}

/* ── Gallery template: a furnished 3D starter room ─────────────────────── */

/** The Gallery template's placed objects: shelving, a table and chairs,
 * crates, and a pair of flanking columns, all from the core pack - real
 * furniture a creator can immediately walk around and rearrange, not an
 * empty box. Reuses `createBlankMapDocument`'s exact room geometry (same
 * wall/floor ids and positions) rather than authoring new geometry, so
 * this starter carries zero risk of the room itself being wrong - only
 * what's placed inside it differs from Blank. */
function galleryObjectInstances(): MapObjectInstance[] {
  const at = (x: number, z: number): MapObjectTransform => ({ position: [x, 0, z] });
  return [
    { id: 'gallery-shelf-l', assetId: 'furn_shelf', floor: 0, transform: at(-6, -8.5) },
    { id: 'gallery-shelf-r', assetId: 'furn_shelf', floor: 0, transform: at(6, -8.5) },
    { id: 'gallery-table', assetId: 'furn_table', floor: 0, transform: at(0, -2) },
    { id: 'gallery-chair-1', assetId: 'furn_chair', floor: 0, transform: at(-1.2, -0.5) },
    { id: 'gallery-chair-2', assetId: 'furn_chair', floor: 0, transform: at(1.2, -0.5) },
    { id: 'gallery-column-l', assetId: 'arch_column', floor: 0, transform: at(-4, 3) },
    { id: 'gallery-column-r', assetId: 'arch_column', floor: 0, transform: at(4, 3) },
    { id: 'gallery-crate-1', assetId: 'furn_crate', floor: 0, transform: at(7, 6) },
    { id: 'gallery-crate-2', assetId: 'furn_crate', floor: 0, transform: at(7.8, 6.5) },
    { id: 'gallery-crate-3', assetId: 'furn_crate', floor: 0, transform: at(6.5, 6.9) },
  ];
}

/** A neutral 3D starter, one room furnished from the core pack: shelving
 * along the back wall, a table and chairs, flanking columns, and a stack of
 * crates - real placed content a creator can immediately preview and build
 * outward from, the 3D counterpart to the Village template's furnished 2D
 * street. */
export function createGalleryMapDocument(projectId: string): MapDocument {
  const blank = createBlankMapDocument(projectId);
  return {
    ...blank,
    objectInstances: galleryObjectInstances(),
    lights: [
      { id: 'gallery-light-warm', floor: 0, position: [0, 2.5, -4], kind: 'point', color: '#ffe8c2', intensity: 1.2, range: 8, castShadow: true },
      { id: 'gallery-light-cool', floor: 0, position: [0, 2.5, 4], kind: 'point', color: '#c2dfff', intensity: 0.9, range: 8 },
    ],
  };
}

/* ── Village template: a top-down 2D starter block ────────────────────── */

const VILLAGE_MAP_WIDTH = 24;
const VILLAGE_MAP_HEIGHT = 14;

/** Ids/colours match Pixel Studio's own built-in grass/sand/water palette
 * (see `PIXEL_TILES` in editor-pixel.tsx) so a creator who opens the tile
 * editor sees the same familiar tiles, plus the extra wall/roof/door/window
 * set the seeded houses are built from. */
const VILLAGE_TILES: Tilemap2DTile[] = [
  { id: 'grass', a: '#3c8c54', b: '#2f7044' },
  { id: 'path', a: '#d8c08a', b: '#c4ab74' },
  { id: 'water', a: '#2c6fd4', b: '#2459ab', solid: true },
  { id: 'wall', a: '#caa06a', b: '#b78a52', solid: true },
  { id: 'roof', a: '#9a4a3a', b: '#7a3a2c', solid: true },
  { id: 'door', a: '#5c3a22', b: '#4a2e1a' },
  { id: 'window', a: '#8ecbe0', b: '#6fb0cc', solid: true },
];

/** Build a square Sprite2D's cells from a list of equal-length pattern rows,
 * mapping each character to a palette colour ('.' is transparent). */
function spriteFromPattern(rows: string[], palette: Record<string, string>): (string | null)[] {
  const cells: (string | null)[] = [];
  for (const row of rows) {
    for (const ch of row) cells.push(ch === '.' ? null : (palette[ch] ?? null));
  }
  return cells;
}

/** A single-tile pine tree, placed as a Tilemap2DEntity. */
const VILLAGE_TREE_SPRITE: Sprite2D = {
  id: 'village-tree',
  size: 12,
  cells: spriteFromPattern(
    [
      '....TTTT....',
      '..TTTTTTTT..',
      '.TTTTTTTTTT.',
      'TTTTTTTTTTTT',
      'TTTTTTTTTTTT',
      '.TTTTTTTTTT.',
      '..TTTTTTTT..',
      '....BBBB....',
      '....BBBB....',
      '....BBBB....',
      '....BBBB....',
      '............',
    ].map((row) => row.slice(0, 12)),
    { T: '#2f7044', B: '#6e4a2c' },
  ),
};

/** Paint a `w`x`h` block of one tile id into a row-major `width`*height cell
 * grid, top-left anchored at (x0, y0). */
function paintRect(
  cells: (string | null)[], width: number, x0: number, y0: number, w: number, h: number, tileId: string,
): void {
  for (let y = y0; y < y0 + h; y++) {
    for (let x = x0; x < x0 + w; x++) cells[y * width + x] = tileId;
  }
}

/** Paint a small 4x3 house silhouette (roof, walled upper storey with
 * windows, a doorway) directly into the ground layer, top-left anchored at
 * (x0, y0) - a tile-built structure rather than a placed sprite, so it reads
 * correctly at any zoom and needs no entity footprint support. */
function paintHouse(cells: (string | null)[], width: number, x0: number, y0: number): void {
  const row = (y: number, tiles: string[]) => tiles.forEach((t, i) => { cells[y * width + x0 + i] = t; });
  row(y0, ['roof', 'roof', 'roof', 'roof']);
  row(y0 + 1, ['wall', 'window', 'window', 'wall']);
  row(y0 + 2, ['wall', 'door', 'door', 'wall']);
}

/** The Village template's 2D layer: a walkable street through a small
 * cluster of houses, a pond, and scattered trees - real starter content a
 * creator can immediately preview and build outward from, not an empty grid. */
export function createVillageTilemap(): Tilemap2D {
  const width = VILLAGE_MAP_WIDTH;
  const height = VILLAGE_MAP_HEIGHT;
  const cells: (string | null)[] = Array(width * height).fill('grass');

  // The street: a two-tile-wide path running the full width.
  paintRect(cells, width, 0, 6, width, 2, 'path');

  // A pond, tucked into a corner away from the street and houses.
  paintRect(cells, width, 1, 9, 4, 3, 'water');

  // Three houses along the north side of the street, two along the south.
  for (const [x, y] of [[2, 1], [10, 1], [17, 1]] as const) paintHouse(cells, width, x, y);
  for (const [x, y] of [[7, 9], [15, 9]] as const) paintHouse(cells, width, x, y);

  const entities: Tilemap2DEntity[] = (
    [[7, 3], [14, 3], [21, 3], [5, 10], [12, 10], [20, 10]] as const
  ).map(([x, y], i) => ({ id: `village-tree-${i}`, spriteId: VILLAGE_TREE_SPRITE.id, x, y, kind: 'decoration' }));

  return {
    width,
    height,
    cells,
    tiles: VILLAGE_TILES,
    sprites: [VILLAGE_TREE_SPRITE],
    entities,
    spawn: { x: 12, y: 6 },
    mode: 'top-down',
  };
}

/** A small top-down village block: a street, a pond, five houses and a
 * scattering of trees, seeded straight into the map's 2D layer. */
export function createVillageMapDocument(projectId: string): MapDocument {
  return {
    id: `${projectId}-map`,
    name: 'Untitled Map',
    version: CURRENT_MAP_VERSION,
    floors: [],
    objectInstances: [],
    stairTriggers: [],
    tilemap2d: createVillageTilemap(),
    settings: { defaultSpawn: { x: 0, y: 0, z: 0, yaw: 0, floor: 0 } },
  };
}

/* ── Platformer template: a 2D side-view starter level ────────────────── */

const PLATFORMER_MAP_WIDTH = 22;
const PLATFORMER_MAP_HEIGHT = 10;

/** Solid ground plus the platformer-only collision kinds
 * (`Platformer2DScene` renders each specially - spikes for `hazard`, a
 * glowing lantern for `checkpoint`, a victory bell for the `bell_goal` id -
 * so a fresh project already has real, recognisable platforming content, not
 * just an empty grid). `pattern: 'girder'` gives the solid ground the same
 * rich industrial-plate rendering the runtime already knows how to draw. */
const PLATFORMER_TILES: Tilemap2DTile[] = [
  { id: 'ground', a: '#4b5563', b: '#374151', solid: true, kind: 'solid', pattern: 'girder', accent: '#94a3b8' },
  { id: 'spikes', a: '#ef4444', b: '#b91c1c', kind: 'hazard' },
  { id: 'lantern', a: '#fbbf24', b: '#b45309', kind: 'checkpoint' },
  { id: 'bell_goal', a: '#facc15', b: '#eab308', kind: 'checkpoint' },
];

/**
 * A short side-view level: solid ground with a flush spike patch to clear,
 * two elevated bonus platforms (one carrying a checkpoint lantern) to
 * explore along the way, and a victory bell at the far end - real starter
 * geometry a creator can immediately preview and build outward from,
 * matching the bar `createVillageTilemap` sets for the top-down template.
 * Every gap a fall can land in has solid ground directly beneath it - the
 * runtime has no "fell off the map" death plane, so an unreachable
 * bottomless drop would strand a player exactly the way the old permanent
 * death soft-lock used to (see `stepPlatformer`'s respawn fix).
 */
export function createPlatformerTilemap(): Tilemap2D {
  const width = PLATFORMER_MAP_WIDTH;
  const height = PLATFORMER_MAP_HEIGHT;
  const cells: (string | null)[] = Array(width * height).fill(null);

  // Ground floor, full width, except a 3-tile spike patch partway across -
  // flush with the walkable path (not a pit to fall into), so touching it
  // is an immediate, guaranteed hazard death rather than a fall with an
  // uncertain landing.
  paintRect(cells, width, 0, height - 1, width, 1, 'ground');
  paintRect(cells, width, 9, height - 1, 3, 1, 'spikes');

  // Two elevated bonus platforms above solid ground (safe to fall from -
  // see the doc comment above), each one tile higher than the last so a
  // single default jump (~2.2 tiles at DEFAULT_PLATFORMER_CONFIG's own
  // jumpImpulse/gravity) comfortably clears the step up to it; the second
  // carries the checkpoint lantern.
  paintRect(cells, width, 13, height - 2, 2, 1, 'ground');
  paintRect(cells, width, 16, height - 3, 4, 1, 'ground');
  cells[(height - 4) * width + 17] = 'lantern';

  // The victory bell at the far end, on its own short rise.
  paintRect(cells, width, 20, height - 1, 2, 1, 'ground');
  cells[(height - 2) * width + 20] = 'bell_goal';

  return {
    width,
    height,
    cells,
    tiles: PLATFORMER_TILES,
    spawn: { x: 1, y: height - 2 },
    mode: 'platformer',
    environmentTheme: 'foundry',
  };
}

/** A short side-view starter level: solid ground, a spike patch to clear,
 * two elevated bonus platforms, a checkpoint, and a goal. */
export function createPlatformerMapDocument(projectId: string): MapDocument {
  return {
    id: `${projectId}-map`,
    name: 'Untitled Map',
    version: CURRENT_MAP_VERSION,
    floors: [],
    objectInstances: [],
    stairTriggers: [],
    tilemap2d: createPlatformerTilemap(),
    settings: { defaultSpawn: { x: 0, y: 0, z: 0, yaw: 0, floor: 0 } },
  };
}

/** Default scene theme for a new Blank project - clean, neutral daylight. */
export function createDefaultProjectConfig(): StudioProjectConfig {
  return {
    theme: {
      background: '#aeb7c2',
      ambient: { intensity: 0.9, color: '#ffffff' },
      hemisphere: { sky: '#dfe7f0', ground: '#3a3f47', intensity: 0.7 },
      directional: { intensity: 1.1, position: [6, 12, 8], castShadow: true },
      postEffects: false,
    },
  };
}
